import { Component, computed, signal } from '@angular/core';
import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MessagesModule } from 'primeng/messages';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { RadioButtonModule } from 'primeng/radiobutton';

import { SMSNode } from '@modules/campaigns/Domain/Nodes/SMSNode';
import { SMSConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { InspectorBase } from '@shared/components/joint-js/components/inspectors/inspector-base';
import { CopiesService } from '@modules/copies/service/copies.service';
import { CopyModel } from '@models/copies/copies.model';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-sms-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    DividerModule,
    SelectButtonModule,
    InputNumberModule,
    InputSwitchModule,
    MessagesModule,
    DropdownModule,
    CalendarModule,
    RadioButtonModule,
    DatePipe,
  ],
  templateUrl: './sms-inspector.component.html',
  styleUrls: ['./sms-inspector.component.scss'],
})
export class SmsInspectorComponent extends InspectorBase<SMSNode> {
  today = new Date();
  expirationDays = signal<number | null>(180);
  expirationAt = signal<Date | null>(null);
  expirationMode = signal<'days' | 'date'>('days');
  sendToAllContacts = signal<boolean>(false);
  selectedTemplateId = signal<string | null>(null);
  templates = signal<CopyModel[]>([]);

  previewMessage = computed(() => {
    const template = this.selectedTemplate();
    if (!template) return '';

    const textBlock = template.blocks?.find(
      (b) => b.type === 'smsText' || b.type === 'text' || b.alias === 'smsText',
    );
    if (!textBlock) return '';

    return textBlock.translations?.[0]?.text ?? '';
  });

  selectedTemplate = computed(() => {
    const templates = this.templates();
    const selectedId = this.selectedTemplateId();
    if (!templates.length || !selectedId) return null;

    return templates.find((t) => t.id === selectedId) ?? null;
  });

  selectedTemplateName = computed(() => {
    return this.selectedTemplate()?.name ?? '';
  });

  editTemplateUrl = computed(() => {
    const templateId = this.selectedTemplateId();
    if (!templateId) return null;

    const tenantId = this.route.snapshot.queryParams['tenant'];
    return `/copy/edit/${templateId}?tenant=${tenantId}`;
  });

  expirationModeOptions = [
    { label: '', value: 'days', icon: 'pi pi-calendar' },
    { label: '', value: 'date', icon: 'pi pi-clock' },
  ];

  constructor(
    private workflow: WorkflowStateService,
    private translate: TranslateService,
    private copiesService: CopiesService,
    private route: ActivatedRoute,
  ) {
    super();
    this.expirationModeOptions = [
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.SMS.CONFIG.EXPIRATION_MODE_DAYS'),
        value: 'days',
        icon: 'pi pi-clock',
      },
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.SMS.CONFIG.EXPIRATION_MODE_DATE'),
        value: 'date',
        icon: 'pi pi-calendar',
      },
    ];
  }

  override get nodeValue(): SMSNode | null {
    const current = this.node();
    if (!current) return null;
    return current.type === NodeType.SendSMS ? (current as SMSNode) : null;
  }

  getTabs() {
    return [
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.SMS.TABS.PREVIEW'),
        value: 'preview' as const,
      },
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.SMS.TABS.CONFIG'),
        value: 'config' as const,
      },
    ];
  }

  loadTemplates() {
    this.copiesService.searchCopies().subscribe((copies: CopyModel[]) => {
      const smsTemplates = copies.filter((c) => c.type === 'sms');
      this.templates.set(smsTemplates);
    });
  }

  override setNode(nodeSignal: any) {
    super.setNode(nodeSignal);
    this.loadTemplates();
  }

  override applyChanges() {
    if (this.mode() === 'preview') return;

    const node = this.nodeValue;
    if (!node) return;

    const mode = this.expirationMode();
    const expAt = mode === 'date' ? this.expirationAt() : null;
    const expDays = expAt ? 0 : this.expirationDays() ?? 0;
    const selectedId = this.selectedTemplateId();

    const updatedConfig: SMSConfig = {
      ...(node.config as SMSConfig),
      copyIds: selectedId ? [selectedId] : [],
      copyId: selectedId || null,
      expirationDays: expDays,
      expirationAt: expAt,
      sendToAllContacts: this.sendToAllContacts(),
    } as SMSConfig;

    const updated = node.cloneWithConfig(updatedConfig) as SMSNode;
    const validation = updated.validate();
    this.errors.set(validation);

    if (!validation.length) {
      this.workflow.addOrUpdateNode(updated);
    }
  }

  onTemplateChange(id: string) {
    this.selectedTemplateId.set(id);
    this.scheduleApplyChanges();
  }

  onExpirationModeChange(mode: 'days' | 'date') {
    this.expirationMode.set(mode);
    this.expirationDays.set(180);
    this.scheduleApplyChanges();
  }

  onExpirationDaysChange(value: number | null) {
    if (value == null || isNaN(value as any)) value = 0;
    if (value < 0) value = 0;
    if (value > 365) value = 365;

    this.expirationDays.set(value);
    this.scheduleApplyChanges();
  }

  onExpirationAtChange(value: Date | null) {
    this.expirationAt.set(value);
    this.scheduleApplyChanges();
  }

  onSendToAllContactsChange(value: boolean) {
    this.sendToAllContacts.set(!!value);
    this.scheduleApplyChanges();
  }

  override refreshFromConfig() {
    const node = this.nodeValue;
    if (!node) return;

    const cfg = (node.config ?? {}) as SMSConfig;

    this.selectedTemplateId.set(cfg.copyIds?.[0] ?? null);
    this.expirationDays.set(cfg.expirationDays ?? 180);
    this.expirationAt.set(cfg.expirationAt ? new Date(cfg.expirationAt) : null);
    this.expirationMode.set(cfg.expirationAt ? 'date' : 'days');
    this.sendToAllContacts.set(cfg.sendToAllContacts ?? false);

    this.errors.set([]);
  }
}
